import React, {useEffect, useMemo, useState} from "react";
import {FullCompanyAppraiser} from "../../api/ApiTypes.ts";
import {useAppraiserAdminApi} from "../../context/ApiContext.tsx";
import {CompanyAppraiserItem} from "../../components/CompanyAppraiserItem.tsx";
import {CompanyAppraiserSkeletonItem} from "../../components/CompanyAppraiserSkeletonItem.tsx";
import {Megaphone, Search} from "lucide-react";

const EmptyState = () => (
    <div className="bg-white rounded-lg shadow p-8 text-center">
        <p className="text-lg text-gray-600">No Appraiser to manage</p>
        <p className="text-sm text-gray-500 mt-2">
            Appraisers that you can manage will appear here when they become available
        </p>
    </div>
);

const FilterEmptyState = () => (
    <div className="bg-white rounded-lg shadow p-8 text-center">
        <p className="text-lg text-gray-600">No appraisers match your filters</p>
        <p className="text-sm text-gray-500 mt-2">
            Try adjusting your search criteria to see more results
        </p>
    </div>
);

export const AppraisersPage: React.FC = () => {
    const [companyAppraisers, setCompanyAppraisers] = useState<FullCompanyAppraiser[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    // Filter states
    const [searchQuery, setSearchQuery] = useState<string>("");

    const appraiserAdminApi = useAppraiserAdminApi();

    useEffect(() => {
        const getCompanyAppraisers = async () => {
            setLoading(true);
            try {
                const data = await appraiserAdminApi.getCompanyAppraisers();
                setCompanyAppraisers(data);
            } catch (err) {
                console.error("Failed to fetch appraisers:", err);
                setCompanyAppraisers([]);
            } finally {
                setLoading(false);
            }
        };
        getCompanyAppraisers();
    }, []);

// Filter appraisers based on search query
    const filteredAppraisers = useMemo(() => {
        return companyAppraisers.filter(appraiser => {
            const query = searchQuery.toLowerCase().trim();

            // If there's no search query, return all appraisers
            if (!query) return true;

            const firstName = appraiser.securityUser.firstName.toLowerCase();
            const lastName = appraiser.securityUser.lastName.toLowerCase();
            const fullName = `${firstName} ${lastName}`.toLowerCase();
            const email = appraiser.securityUser.email.toLowerCase();
            const address = appraiser.officeAddress.toLowerCase();

            // Check if the appraiser matches the search query
            return fullName.includes(query) ||
                email.includes(query) ||
                address.includes(query);
        });
    }, [companyAppraisers, searchQuery]);

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
    };

    const clearFilters = () => {
        setSearchQuery("");
    };

    return (
        <div className="max-w-6xl mx-auto px-4 py-6">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-slate-800">Appraisers</h1>
                <div className="text-sm text-slate-500">
                    {filteredAppraisers.length} {filteredAppraisers.length === 1 ? 'appraiser' : 'appraisers'} found
                </div>
            </div>

            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                <p className="text-sm text-blue-700 inline-flex items-center py-1">
                    <strong className="inline-flex items-center px-2"><Megaphone/></strong> If you need to remove an appraiser please send an email to valuationvm@stewart.com
                </p>
            </div>

            {/* Search and Filters */}
            <div className="bg-white p-4 rounded-lg shadow-sm mb-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Search input */}
                    <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Search className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                            type="text"
                            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            placeholder="Search by name, email, zip code..."
                            value={searchQuery}
                            onChange={handleSearchChange}
                        />
                    </div>

                    {/* Clear filters button */}
                    <div className="flex items-center">
                        <button
                            onClick={clearFilters}
                            className="text-sm text-blue-600 hover:text-blue-800 focus:outline-none"
                            disabled={!searchQuery}
                        >
                            Clear filters
                        </button>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="space-y-4">
                    {[...Array(3)].map((_, i) => (
                        <CompanyAppraiserSkeletonItem key={i} />
                    ))}
                </div>
            ) : companyAppraisers.length > 0 ? (
                filteredAppraisers.length > 0 ? (
                    <div className="space-y-4">
                        {filteredAppraisers.map((appraiser) => (
                            <CompanyAppraiserItem key={appraiser.appraiserInfo.id} appraiser={appraiser} />
                        ))}
                    </div>
                ) : (
                    <FilterEmptyState />
                )
            ) : (
                <EmptyState />
            )}
        </div>
    );
}